import styles from './index.module.css'

import cn from 'clsx'
import { LuChartColumn } from '@react-icons/all-files/lu/LuChartColumn'
import { LuChevronDown } from '@react-icons/all-files/lu/LuChevronDown'
import { LuChevronRight } from '@react-icons/all-files/lu/LuChevronRight'
import { LuForward } from '@react-icons/all-files/lu/LuForward'
import { LuInfo } from '@react-icons/all-files/lu/LuInfo'
import { LuX } from '@react-icons/all-files/lu/LuX'
import React, { useCallback, useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import api from '@disco/api/server'
import { TRACK_SHARING } from '@disco/constants/FeatureTypes'
import { sendGenericError } from '@disco/state-management/actions/error-actions'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { formatTimestampShort } from '@disco/utils/time-utils'

import type { Track } from 'types/track'

import {
  Avatar,
  Box,
  Dialog,
  Flex,
  Grid,
  IconButton,
  Link,
  Skeleton,
  Table,
  Tabs,
  Text,
  Tooltip,
} from 'components/ds'

import SortableHeader from 'components/common/sortable-header'
import { useTableSort } from 'components/common/sortable-header/use-table-sort'

import { useDispatch, useSelector } from 'hooks/use-redux'

import type {
  AggregatedStats,
  SourceRow,
  TransformedListenerData,
} from './helpers'
import {
  getAggregates,
  getListenerDisplayName,
  getListenerSortValue,
  getListenerSourceBreakdown,
  getSourceSortValue,
  makeSourceData,
  transformListenerData,
} from './helpers'

import SourceLink from './source-link'

type Props = {
  onClose: (...args: Array<any>) => any
  track: Track
}

/** A track can be shared from many playlists, so cap the expanded list. */
const MAX_VISIBLE_SOURCES = 4

const renderListener = (row: TransformedListenerData) => {
  const isUnattributed = row.id === -1
  const username = getListenerDisplayName(row)
  const initials = username
    .split(' ')
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? '')
    .join('')

  const avatar = (
    <Avatar
      size="2"
      radius="full"
      fallback={initials}
      className={styles.listenerAvatar}
    />
  )
  const nameEl = (
    <Flex align="center" gap="1" minWidth="0" flexGrow="1">
      <Tooltip content={username}>
        <Text size="2" truncate>
          {username}
        </Text>
      </Tooltip>
      {isUnattributed && (
        <Tooltip content="Combined activity from listeners who aren't linked to a contact.">
          {/* This tooltip is the only place the row's meaning is explained, so
              its trigger must be reachable by keyboard — Radix wires focus
              handlers into its child but doesn't make it focusable, and a
              plain span never is. */}
          <button
            type="button"
            className={styles.infoIcon}
            aria-label="About Other Recipients"
          >
            <LuInfo size={14} />
          </button>
        </Tooltip>
      )}
    </Flex>
  )

  return !isUnattributed ? (
    <RouterLink className={styles.listener} to={`/contacts/${row.id}`}>
      {avatar}
      {nameEl}
    </RouterLink>
  ) : (
    <span className={styles.listener}>
      {avatar}
      {nameEl}
    </span>
  )
}

const Stats = ({ onClose, track }: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const isTrackSharingEnabled = useFeatureEnabled(TRACK_SHARING)
  const isPhone = useSelector(isDeviceTypePhone)

  const [listenerData, setListenerData] = useState<
    TransformedListenerData[] | null
  >(null)
  const [sourceData, setSourceData] = useState<SourceRow[] | null>(null)
  const [aggregatedStats, setAggregatedStats] =
    useState<AggregatedStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [noData, setNoData] = useState(false)

  useEffect(() => {
    const fetchListenerData = async () => {
      setLoading(true)

      try {
        const rawListenerData = await api.getTrackStats(track.id)

        if (!rawListenerData || rawListenerData.length === 0) {
          setLoading(false)
          setNoData(true)
          return
        }

        const listeners = transformListenerData(
          rawListenerData,
          isTrackSharingEnabled,
        )
        setListenerData(listeners)
        setAggregatedStats(getAggregates(listeners))
        setSourceData(makeSourceData(rawListenerData, track))
        setLoading(false)
      } catch (error) {
        setLoading(false)
        dispatch(sendGenericError(error))
      }
    }

    fetchListenerData()
  }, [dispatch, isTrackSharingEnabled, track])

  const listenerSort = useTableSort(listenerData, getListenerSortValue)
  const sourceSort = useTableSort(sourceData, getSourceSortValue)

  const [expandedRows, setExpandedRows] = useState<Set<number>>(() => new Set())
  const [rowsShowingAllSources, setRowsShowingAllSources] = useState<
    Set<number>
  >(() => new Set())

  const toggleInSet = (
    setState: React.Dispatch<React.SetStateAction<Set<number>>>,
    rowId: number,
  ) =>
    setState(previous => {
      const next = new Set(previous)
      if (next.has(rowId)) {
        next.delete(rowId)
      } else {
        next.add(rowId)
      }
      return next
    })

  const toggleRowExpanded = useCallback(
    (rowId: number) => toggleInSet(setExpandedRows, rowId),
    [],
  )

  const toggleShowAllSources = useCallback(
    (rowId: number) => toggleInSet(setRowsShowingAllSources, rowId),
    [],
  )

  const numCards = isTrackSharingEnabled ? 4 : 3
  const gridColumns = isPhone ? '2' : String(numCards)

  const renderSourceBreakdownRows = (row: TransformedListenerData) => {
    const sources = getListenerSourceBreakdown(row, track)

    if (sources.length === 0) {
      return (
        <Table.Row className={styles.detailRow}>
          <Table.Cell />
          <Table.Cell colSpan={5}>
            <Text size="2" color="gray">
              No source data available.
            </Text>
          </Table.Cell>
        </Table.Row>
      )
    }

    const showAll = rowsShowingAllSources.has(row.id)
    const canToggle = sources.length > MAX_VISIBLE_SOURCES
    const visibleSources = showAll
      ? sources
      : sources.slice(0, MAX_VISIBLE_SOURCES)
    const remaining = sources.length - visibleSources.length

    return (
      <>
        {visibleSources.map(source => (
          <Table.Row
            key={source.Source.sourceText}
            className={styles.detailRow}
          >
            <Table.Cell />
            <Table.Cell>
              <Tooltip content={source.Source.sourceText}>
                <span className={styles.sourceCell}>
                  <SourceLink source={source.Source} />
                </span>
              </Tooltip>
            </Table.Cell>
            <Table.Cell justify="end">{source['Page Views']}</Table.Cell>
            <Table.Cell justify="end">{source['Downloads/Saves']}</Table.Cell>
            <Table.Cell justify="end">{source['Streams']}</Table.Cell>
            <Table.Cell />
          </Table.Row>
        ))}
        {canToggle && (
          <Table.Row className={styles.detailRow}>
            <Table.Cell />
            <Table.Cell colSpan={5} justify="end">
              <Link asChild>
                <button
                  type="button"
                  onClick={() => toggleShowAllSources(row.id)}
                >
                  {showAll
                    ? 'Show fewer sources'
                    : `Show ${remaining} more source${
                        remaining === 1 ? '' : 's'
                      }`}
                </button>
              </Link>
            </Table.Cell>
          </Table.Row>
        )}
      </>
    )
  }

  const renderListenersTable = () => (
    <Table.Root mt="3" className={cn(styles.statsTable, styles.listenersTable)}>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell />
          <SortableHeader
            label="Name"
            column="name"
            align="start"
            activeColumn={listenerSort.sortColumn}
            direction={listenerSort.sortDirection}
            onSort={listenerSort.toggleSort}
          />
          <SortableHeader
            label="Page Views"
            column="pageViews"
            activeColumn={listenerSort.sortColumn}
            direction={listenerSort.sortDirection}
            onSort={listenerSort.toggleSort}
          />
          <SortableHeader
            label="Downloads/Saves"
            column="downloads"
            activeColumn={listenerSort.sortColumn}
            direction={listenerSort.sortDirection}
            onSort={listenerSort.toggleSort}
          />
          <SortableHeader
            label="Streams"
            column="streams"
            activeColumn={listenerSort.sortColumn}
            direction={listenerSort.sortDirection}
            onSort={listenerSort.toggleSort}
          />
          <SortableHeader
            label="Last Activity"
            column="lastActivity"
            align="start"
            activeColumn={listenerSort.sortColumn}
            direction={listenerSort.sortDirection}
            onSort={listenerSort.toggleSort}
          />
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {listenerSort.sortedRows.map(row => {
          const isExpanded = expandedRows.has(row.id)

          return (
            <React.Fragment key={row.id}>
              <Table.Row
                className={isExpanded ? styles.expandedRow : undefined}
              >
                <Table.Cell>
                  <IconButton
                    type="button"
                    variant="ghost"
                    color="gray"
                    size="1"
                    aria-label={
                      isExpanded
                        ? `Collapse ${getListenerDisplayName(row)}`
                        : `Expand ${getListenerDisplayName(row)}`
                    }
                    aria-expanded={isExpanded}
                    onClick={() => toggleRowExpanded(row.id)}
                  >
                    {isExpanded ? (
                      <LuChevronDown size={14} />
                    ) : (
                      <LuChevronRight size={14} />
                    )}
                  </IconButton>
                </Table.Cell>
                <Table.Cell>{renderListener(row)}</Table.Cell>
                <Table.Cell justify="end">{row['Page Views']}</Table.Cell>
                <Table.Cell justify="end">{row['Downloads/Saves']}</Table.Cell>
                <Table.Cell justify="end">{row['Streams']}</Table.Cell>
                <Table.Cell>
                  {row['Last Activity']
                    ? formatTimestampShort(row['Last Activity'])
                    : '—'}
                </Table.Cell>
              </Table.Row>
              {isExpanded && renderSourceBreakdownRows(row)}
            </React.Fragment>
          )
        })}
      </Table.Body>
    </Table.Root>
  )

  const renderSourceTable = () => (
    <Table.Root mt="3" className={styles.statsTable}>
      <Table.Header>
        <Table.Row>
          <SortableHeader
            label="Source"
            column="source"
            align="start"
            activeColumn={sourceSort.sortColumn}
            direction={sourceSort.sortDirection}
            onSort={sourceSort.toggleSort}
          />
          <SortableHeader
            label="Page Views"
            column="pageViews"
            activeColumn={sourceSort.sortColumn}
            direction={sourceSort.sortDirection}
            onSort={sourceSort.toggleSort}
          />
          <SortableHeader
            label="Downloads/Saves"
            column="downloads"
            activeColumn={sourceSort.sortColumn}
            direction={sourceSort.sortDirection}
            onSort={sourceSort.toggleSort}
          />
          <SortableHeader
            label="Streams"
            column="streams"
            activeColumn={sourceSort.sortColumn}
            direction={sourceSort.sortDirection}
            onSort={sourceSort.toggleSort}
          />
          <SortableHeader
            label="Listeners"
            column="listeners"
            activeColumn={sourceSort.sortColumn}
            direction={sourceSort.sortDirection}
            onSort={sourceSort.toggleSort}
          />
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {sourceSort.sortedRows.map((row, index) => (
          <Table.Row key={index}>
            <Table.Cell>
              <Tooltip content={row['Source'].sourceText}>
                <span className={styles.sourceCell}>
                  <SourceLink source={row['Source']} />
                </span>
              </Tooltip>
            </Table.Cell>
            <Table.Cell justify="end">{row['Page Views']}</Table.Cell>
            <Table.Cell justify="end">{row['Downloads/Saves']}</Table.Cell>
            <Table.Cell justify="end">{row['Streams']}</Table.Cell>
            <Table.Cell justify="end">{row.uniqueListeners.length}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )

  const renderContents = () => {
    if (noData) {
      return (
        <Flex direction="column" align="center" gap="3" py="9">
          <Flex align="center" justify="center" className={styles.emptyIcon}>
            <LuChartColumn size={24} />
          </Flex>
          <Text size="3" weight="bold" mt="2">
            No stats yet
          </Text>
          <Text size="2" color="gray" align="center">
            Stats will show up here once {track.name} starts getting plays.
          </Text>
          <Link asChild>
            <RouterLink
              to={`/explorer/tracks/${track.id}/share`}
              className={styles.shareLink}
            >
              <LuForward size={16} />
              <span className={styles.shareLinkText}>Share track</span>
            </RouterLink>
          </Link>
        </Flex>
      )
    }

    if (loading) {
      return (
        <>
          <Grid columns={gridColumns} gap="3" mb="5">
            {Array.from({ length: numCards }).map((_, i) => (
              <Skeleton key={i} height="120px" />
            ))}
          </Grid>
          <Skeleton height="var(--font-size-1)" width="120px" mb="3" />
          <Flex direction="column" gap="3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} height="40px" />
            ))}
          </Flex>
        </>
      )
    }

    const statCards = [
      ...(isTrackSharingEnabled
        ? [
            {
              label: 'Unique Listeners',
              value: aggregatedStats?.uniqueListeners,
            },
          ]
        : []),
      { label: 'Page Views', value: aggregatedStats?.pageViews },
      { label: 'Downloads/Saves', value: aggregatedStats?.downloads },
      { label: 'Streams', value: aggregatedStats?.streams },
    ]

    return (
      <>
        <Grid columns={gridColumns} gap="3" mb="5">
          {statCards.map(card => (
            <Box key={card.label} className={styles.statCard}>
              <Text size="2" color="gray" className={styles.statCardLabel}>
                {card.label}
              </Text>
              <Text size="7" weight="bold" className={styles.statCardValue}>
                {card.value}
              </Text>
            </Box>
          ))}
        </Grid>

        <Tabs.Root defaultValue="listeners">
          <Tabs.List>
            <Tabs.Trigger value="listeners">Listeners</Tabs.Trigger>
            <Tabs.Trigger value="source">Source</Tabs.Trigger>
          </Tabs.List>
          <Tabs.Content value="listeners">
            {renderListenersTable()}
          </Tabs.Content>
          <Tabs.Content value="source">{renderSourceTable()}</Tabs.Content>
        </Tabs.Root>
      </>
    )
  }

  if (!track) return null

  return (
    <Dialog.Root open onOpenChange={open => !open && onClose()}>
      <Dialog.Content
        maxWidth="900px"
        className={styles.dialogContent}
        aria-describedby={undefined}
      >
        <div className={styles.dialogHeader}>
          <Dialog.Close className={styles.closeButton}>
            <IconButton
              type="button"
              variant="ghost"
              color="gray"
              size="2"
              aria-label="Close"
            >
              <LuX size={18} />
            </IconButton>
          </Dialog.Close>
          <Text
            size="1"
            weight="bold"
            color="gray"
            className={styles.sectionLabel}
          >
            TRACK STATS
          </Text>
          <Dialog.Title mt="1" mb="0">
            {track.name}
          </Dialog.Title>
        </div>

        <div className={styles.dialogBody}>{renderContents()}</div>

        {!loading && !noData && (
          <Text size="2" color="gray" className={styles.footer}>
            To view more activity for your recipients, and manage their details,
            visit{' '}
            <Link asChild>
              <RouterLink to="/contacts/list">Contacts</RouterLink>
            </Link>
          </Text>
        )}
      </Dialog.Content>
    </Dialog.Root>
  )
}

export default React.memo<Props>(Stats)
